import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input, OnDestroy, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { AuthService } from '../../../auth/auth.service';
import { TokenService } from '../../../auth/token.service';
import { ToasterService } from '../../services/toaster.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-email-verification-modal',
  imports: [FormsModule],
  templateUrl: './email-verification-modal.component.html',
  styleUrl: './email-verification-modal.component.scss',
})
export class EmailVerificationModalComponent implements OnDestroy {
  private readonly _authService = inject(AuthService);
  private readonly _tokenService = inject(TokenService);
  private readonly _toaster = inject(ToasterService);
  private readonly _cd = inject(ChangeDetectorRef);

  /** Email address to confirm (shown read-only in step 1, masked in step 2). */
  readonly email = input<string>('');
  /** Override the default modal title (step 1). */
  readonly title = input<string>('Verify your email');
  /** Override the default description copy (step 1). */
  readonly description = input<string>('');
  /** Fires after the user successfully verifies (so the host can refetch + replay). */
  readonly verified = output<void>();
  readonly closeModal = output<void>();

  /** Step gate: false → confirm-email screen, true → enter-code screen */
  protected readonly showCodeBox = signal(false);
  protected readonly verificationCode = signal('');
  protected readonly sendingCode = signal(false);
  protected readonly submitting = signal(false);
  protected readonly resending = signal(false);
  protected readonly resendCooldown = signal(false);
  protected readonly resendCountdown = signal(60);
  private resendInterval: ReturnType<typeof setInterval> | undefined;

  ngOnDestroy(): void {
    if (this.resendInterval) clearInterval(this.resendInterval);
  }

  protected get maskedEmail(): string {
    const value = this.email();
    if (!value) return '';
    const [local, domain] = value.split('@');
    if (!domain || local.length <= 2) return value;
    const masked = local[0] + '•'.repeat(Math.max(local.length - 2, 1)) + local[local.length - 1];
    return `${masked}@${domain}`;
  }

  protected sendCode(isResend = false): void {
    const token = this._tokenService.getToken;
    if (!token) return;
    if (isResend && (this.resendCooldown() || this.resending())) return;
    if (isResend) this.resending.set(true);
    else this.sendingCode.set(true);
    this._authService.verifyEmailSignup(token)
      .pipe(finalize(() => {
        this.sendingCode.set(false);
        this.resending.set(false);
      }))
      .subscribe({
        next: () => {
          if (isResend) {
            this._toaster.success('Verification code resent.', 'Sent');
          } else {
            this.showCodeBox.set(true);
          }
          this.startResendCooldown();
        },
        error: () => {
          this._toaster.error('Could not send the verification email. Please try again.', 'Error');
        },
      });
  }

  protected onCodeInput(value: string): void {
    this.verificationCode.set(value);
  }

  protected verify(): void {
    const code = this.verificationCode().trim();
    if (code.length < 6 || !/^[0-9]+$/.test(code)) return;
    const token = this._tokenService.getToken;
    if (!token) return;
    this.submitting.set(true);
    this._authService.confirmEmail(code, token)
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: () => {
          this.verified.emit();
        },
        error: () => {
          this._toaster.error('Invalid or expired verification code.', 'Error');
        },
      });
  }

  protected close(): void {
    this.showCodeBox.set(false);
    this.closeModal.emit();
  }

  private startResendCooldown(): void {
    this.resendCooldown.set(true);
    this.resendCountdown.set(60);
    if (this.resendInterval) clearInterval(this.resendInterval);
    this.resendInterval = setInterval(() => {
      const next = this.resendCountdown() - 1;
      this.resendCountdown.set(next);
      if (next <= 0) {
        this.resendCooldown.set(false);
        if (this.resendInterval) clearInterval(this.resendInterval);
      }
      this._cd.markForCheck();
    }, 1000);
  }
}
